/**
 * Le sujet de bac du dimanche, côté parent (`core-api` §3.32, `web-pwa` §17.12).
 *
 * Le rituel en choisit un en milieu de semaine, parmi les sujets **publiés** du
 * recueil de la classe dont le chapitre a été vu en classe, sans jamais
 * reprendre un sujet déjà donné. Le parent le voit à l'avance et peut en
 * changer tant que le chronomètre n'est pas parti ; l'élève choisit l'heure,
 * pas le principe.
 *
 * **Le parent tient le chronomètre.** La durée est celle de l'épreuve
 * officielle, au prorata des points de l'exercice quand le barème est connu,
 * sinon celle de l'éditeur ; elle se fige au départ. Pendant l'épreuve, **le
 * corrigé n'arrive pas jusqu'à cet écran** : le serveur le retire de la
 * réponse (règle 2), il ne se cache pas ici.
 *
 * La carte ne s'affiche que pour un élève qui passe le bac : pour un collégien,
 * la réponse dit `concerne: false` et l'écran ne montre rien. Aucun prénom n'y
 * est comparé.
 */

import { useCallback, useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { SujetDuDimancheOut } from "@/api/schema";
import {
  dureeLisible,
  etatDuChronometre,
  heureLisible,
  ligneDuChronometre,
} from "@/ui/chronometre";
import { enClairCourt } from "@/ui/dates";
import { LiensDImpression } from "@/ui/impression";
import { Button, Card, ErrorNote } from "@/ui/primitives";

/** Rafraîchit la ligne du chronomètre sans rien demander au serveur. */
function useMaintenant(actif: boolean): Date {
  const [maintenant, setMaintenant] = useState(() => new Date());
  useEffect(() => {
    if (!actif) return undefined;
    const minuterie = window.setInterval(() => setMaintenant(new Date()), 30_000);
    return () => window.clearInterval(minuterie);
  }, [actif]);
  return maintenant;
}

export function SujetDuDimanche({
  childId,
  prenom,
  apresChangement,
}: {
  childId: string;
  prenom: string | undefined;
  /** Pour que la liste des sujets donnés, plus bas, se relise. */
  apresChangement: () => void;
}) {
  const [etat, setEtat] = useState<SujetDuDimancheOut | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [changer, setChanger] = useState(false);

  const charger = useCallback(async () => {
    const lu = await call("sujetDuDimancheDeLEnfant", {
      params: { child_id: childId },
    });
    setEtat(lu);
  }, [childId]);

  useEffect(() => {
    setEtat(null);
    setErreur(null);
    void charger().catch((echec) =>
      setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible."),
    );
  }, [charger]);

  const sujet = etat?.sujet ?? null;
  const chrono = sujet ? etatDuChronometre(sujet) : "pas_demarre";
  const maintenant = useMaintenant(chrono === "en_cours");

  if (erreur) return <ErrorNote>{erreur}</ErrorNote>;
  if (!etat || !etat.concerne) return null;

  const agir = async (action: () => Promise<SujetDuDimancheOut | unknown>) => {
    setEnvoi(true);
    setErreur(null);
    try {
      await action();
      await charger();
      setChanger(false);
      apresChangement();
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : "L'action a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  const choisir = (identifiant: string | null) =>
    agir(() =>
      call("choisirLeSujetDuDimanche", {
        params: { child_id: childId },
        body: { sujet: identifiant },
      }),
    );
  const chronometre = (quoi: "demarrerLeChronometre" | "arreterLeChronometre") =>
    sujet
      ? agir(() =>
          call(quoi, {
            params: { child_id: childId, sujet_donne_id: sujet.id },
          }),
        )
      : Promise.resolve();

  const candidats = etat.candidats ?? [];
  const dimanche = etat.dimanche ? enClairCourt(etat.dimanche) : "dimanche";
  const ligne = sujet ? ligneDuChronometre(sujet, maintenant) : null;
  const modifiable = sujet != null && chrono === "pas_demarre" && !sujet.rendu_at;

  return (
    <Card>
      <p className="card__title">Le sujet de bac du dimanche</p>
      {sujet ? (
        <>
          <p>
            {dimanche} : {sujet.titre} <span className="muted small">— {sujet.chapitre_titre}</span>
          </p>
          <p className="muted small">
            {dureeLisible(sujet.duree_officielle_min ?? sujet.duree_prevue_min)}
            {sujet.bareme_source ? ` — barème : ${sujet.bareme_source}` : " — sans barème publié"}
            {sujet.propose_par_le_rituel ? " — choisi par le tuteur" : " — choisi par un parent"}
          </p>
          <p className="muted small">
            {heureLisible(sujet.heure_choisie)
              ? `${prenom ?? "L'élève"} a choisi ${heureLisible(sujet.heure_choisie)}.`
              : `${prenom ?? "L'élève"} n'a pas encore choisi l'heure.`}
          </p>
          {ligne ? <p>{ligne}</p> : null}
          {/* Le sujet seul : le corrigé ne s'imprime qu'une fois l'épreuve
              finie, et le serveur refuse de le rendre avant. */}
          <LiensDImpression
            cible={{ quoi: "sujet", childId, sujetDonneId: sujet.id }}
            corrige={(sujet.corrige_urls ?? []).length > 0}
          />
          <div className="list__actions">
            {chrono === "pas_demarre" && !sujet.rendu_at ? (
              <Button
                type="button"
                disabled={envoi}
                onClick={() => void chronometre("demarrerLeChronometre")}
              >
                Démarrer le chronomètre
              </Button>
            ) : null}
            {chrono === "en_cours" ? (
              <Button
                type="button"
                disabled={envoi}
                onClick={() => void chronometre("arreterLeChronometre")}
              >
                Arrêter le chronomètre
              </Button>
            ) : null}
            {modifiable && candidats.length > 0 ? (
              <Button type="button" variant="quiet" onClick={() => setChanger(!changer)}>
                {changer ? "Garder ce sujet" : "Changer de sujet"}
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <p className="muted small">
            {etat.raison ?? `Aucun sujet n'est encore prévu pour ${dimanche}.`}
          </p>
          {candidats.length > 0 ? (
            <Button type="button" disabled={envoi} onClick={() => void choisir(null)}>
              Prévoir un sujet pour {dimanche}
            </Button>
          ) : null}
        </>
      )}

      {changer && modifiable ? (
        <ul className="list">
          {candidats.map((candidat) => (
            <li key={candidat.identifiant} className="list__item">
              <p>
                {candidat.titre} <span className="muted small">— {candidat.chapitre_titre}</span>
              </p>
              <p className="muted small">
                {dureeLisible(candidat.duree_prevue_min)}
                {candidat.bareme ? " — barème publié" : " — sans barème publié"}
              </p>
              <Button
                type="button"
                variant="quiet"
                disabled={envoi}
                onClick={() => void choisir(candidat.identifiant)}
              >
                Choisir celui-ci
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}
